/**
 * Quick-view drawer.
 *
 * A "View options" button on a multi-variant product card
 * ([data-quick-view-open][data-product-url]) opens a right-side drawer. The
 * drawer fetches sections/quick-view.liquid via the Section Rendering API for
 * that product, injects it, and wires variant selection + a quantity stepper.
 * Add-to-cart is handled by the theme's <product-form-component> (which hydrates
 * on insertion and auto-opens the cart drawer); when the cart drawer opens we
 * close the quick view so they don't overlap.
 */
const CLOSE_ICON =
  '<svg viewBox="0 0 20 20" fill="none" aria-hidden="true"><path d="M5 5l10 10M15 5L5 15" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>';

let shell = null;
let overlay = null;
let panel = null;
let content = null;
let lastFocus = null;

function buildShell() {
  if (shell) return;
  shell = document.createElement('div');
  shell.className = 'tqv';
  shell.innerHTML = `
    <div class="tqv__overlay" data-tqv-close></div>
    <aside class="tqv__panel" role="dialog" aria-modal="true" aria-label="Product options" tabindex="-1">
      <button type="button" class="tqv__close" data-tqv-close aria-label="Close">${CLOSE_ICON}</button>
      <div class="tqv__content"></div>
    </aside>`;
  document.body.appendChild(shell);
  overlay = shell.querySelector('.tqv__overlay');
  panel = shell.querySelector('.tqv__panel');
  content = shell.querySelector('.tqv__content');

  shell.addEventListener('click', (event) => {
    if (event.target.closest('[data-tqv-close]')) close();
  });
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && shell.hasAttribute('data-open')) close();
  });
  // Closing the quick view when the cart drawer (a theme-drawer) opens keeps
  // the two drawers from stacking on top of each other.
  document.addEventListener('theme-drawer:open', () => {
    if (shell.hasAttribute('data-open')) close();
  });
  // Close on a successful add-to-cart regardless of cart type (drawer, page or
  // notification): the theme fires this when the cart lines change.
  document.addEventListener('shopify:cart:lines-update', () => {
    if (shell.hasAttribute('data-open')) close();
  });
}

function openShell() {
  // Commit the closed state (panel off-screen) before flipping data-open so the
  // transform transitions in on the very first open too.
  void panel.offsetWidth;
  shell.setAttribute('data-open', '');
  document.documentElement.style.overflow = 'hidden';
  requestAnimationFrame(() => panel.focus());
}

function close() {
  if (!shell) return;
  shell.removeAttribute('data-open');
  document.documentElement.style.overflow = '';
  if (lastFocus && typeof lastFocus.focus === 'function') lastFocus.focus();
}

async function open(url, trigger) {
  buildShell();
  lastFocus = trigger || document.activeElement;
  content.innerHTML = '<div class="tqv__loading mono-14">Loading&hellip;</div>';
  openShell();

  const base = url.split('#')[0];
  const fetchUrl = base + (base.includes('?') ? '&' : '?') + 'section_id=quick-view';

  try {
    const response = await fetch(fetchUrl);
    if (!response.ok) throw new Error('Bad response');
    const html = await response.text();
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const product = parsed.querySelector('[data-tqv-product]');
    if (!product) throw new Error('No content');
    content.innerHTML = '';
    content.appendChild(product);
    wire(content, trigger && trigger.dataset ? trigger.dataset.selectedColor : null);
    injectRecharge(content, base);
  } catch (error) {
    content.innerHTML = '<div class="tqv__loading mono-14">Unable to load product.</div>';
  }
}

/**
 * Pull the existing Recharge subscription widget (a theme app-extension block)
 * from the full product page and drop it into the drawer's slot, inside the
 * add-to-cart form so its selling_plan input submits with the order. Best
 * effort: if the widget can't be found, the slot stays hidden.
 */
async function injectRecharge(root, productUrl) {
  const slot = root.querySelector('[data-tqv-recharge]');
  if (!slot) return;
  try {
    const response = await fetch(productUrl);
    if (!response.ok) return;
    const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
    const widget = doc.querySelector(
      'recharge-subscription-widget, [id*="recharge" i], [class*="recharge" i], [id*="rc_widget" i], [class*="rc_widget" i]'
    );
    if (!widget) return;
    const block = widget.closest('.shopify-app-block, [id^="shopify-block"]') || widget;
    slot.innerHTML = '';
    slot.appendChild(block.cloneNode(true));
    slot.hidden = false;
    // Nudge Recharge to (re)initialise the injected markup where possible.
    if (window.rechargeWidgets && typeof window.rechargeWidgets.init === 'function') {
      window.rechargeWidgets.init();
    }
    document.dispatchEvent(new CustomEvent('recharge:mount', { detail: { container: slot } }));
  } catch (error) {
    /* leave the slot hidden */
  }
}

function wire(root, preselectColor) {
  const dataEl = root.querySelector('[data-tqv-variants]');
  let variants = [];
  try {
    variants = dataEl ? JSON.parse(dataEl.textContent) : [];
  } catch (error) {
    variants = [];
  }

  const variantInput = root.querySelector('input[name="id"]');
  const groups = Array.from(root.querySelectorAll('.tqv-opt[data-option-index]'));

  // Seed the current selection (one value per option) from aria-pressed.
  const selection = [];
  groups.forEach((group) => {
    const index = Number(group.dataset.optionIndex);
    const active =
      group.querySelector('.tqv-opt__btn[aria-pressed="true"]') || group.querySelector('.tqv-opt__btn');
    selection[index] = active ? active.dataset.value : null;
  });

  // Which option is the colour, and pre-select the colour chosen on the card.
  const colorGroup = root.querySelector('.tqv-opt[data-color-option]');
  const colorIndex = colorGroup ? Number(colorGroup.dataset.optionIndex) : -1;
  if (preselectColor && colorIndex > -1) {
    const want = preselectColor.trim().toLowerCase();
    colorGroup.querySelectorAll('.tqv-opt__btn').forEach((b) => {
      if ((b.dataset.value || '').trim().toLowerCase() === want) selection[colorIndex] = b.dataset.value;
    });
  }

  const findVariant = () =>
    variants.find((variant) => variant.options.every((opt, i) => opt === selection[i]));

  const setMoney = (el, value) => {
    if (!el) return;
    if (value == null) {
      el.setAttribute('hidden', '');
    } else {
      el.removeAttribute('hidden');
      el.textContent = value;
    }
  };

  const update = () => {
    groups.forEach((group) => {
      const index = Number(group.dataset.optionIndex);
      group.querySelectorAll('.tqv-opt__btn').forEach((btn) => {
        btn.setAttribute('aria-pressed', String(btn.dataset.value === selection[index]));
      });
      const selectedLabel = group.querySelector('[data-tqv-selected]');
      if (selectedLabel && selection[index] != null) selectedLabel.textContent = selection[index];
    });

    const variant = findVariant();
    if (!variant) return;

    if (variantInput) {
      variantInput.value = variant.id;
      variantInput.disabled = false;
    }

    setMoney(root.querySelector('[data-tqv-amount]'), variant.price);
    setMoney(root.querySelector('[data-tqv-compare]'), variant.compare);
    setMoney(root.querySelector('[data-tqv-atc-amount]'), variant.price);
    setMoney(root.querySelector('[data-tqv-atc-compare]'), variant.compare);

    // Filter the gallery exactly like the PDP grouping:
    //   • ALT-tagged image  -> show only if its tag is one of the selected
    //     variant's option values (colour or size);
    //   • native per-variant image -> show only if it's the selected colour's;
    //   • shared image (neither) -> always show.
    const selValues = selection.filter(Boolean).map((v) => v.toLowerCase());
    const selColor = colorIndex > -1 && selection[colorIndex] ? selection[colorIndex].toLowerCase() : '';
    const gallery = root.querySelector('.tqv-product__gallery');
    const mediaCells = root.querySelectorAll('[data-tqv-media]');
    if (selValues.length && mediaCells.length) {
      let anyShown = false;
      mediaCells.forEach((cell) => {
        const shade = (cell.dataset.shade || '').toLowerCase();
        const color = (cell.dataset.color || '').toLowerCase();
        const native = cell.dataset.native === 'true';
        let show;
        if (shade) {
          show = selValues.includes(shade);
        } else if (native) {
          show = !selColor || color === selColor;
        } else {
          show = true;
        }
        cell.hidden = !show;
        if (show) anyShown = true;
      });
      if (!anyShown) mediaCells.forEach((cell) => (cell.hidden = false));
    }

    // Lead with the selected variant's featured image and scroll to it, exactly
    // like the PDP (whose default is to surface the variant's image on top).
    // This is what makes switching colour visibly change the gallery even when
    // the product has no ALT tags to filter on.
    if (gallery && variant.media_id != null) {
      const lead = gallery.querySelector('[data-media-id="' + variant.media_id + '"]');
      if (lead) {
        lead.hidden = false;
        if (lead !== gallery.firstElementChild) gallery.insertBefore(lead, gallery.firstElementChild);
        gallery.scrollTop = 0;
      }
    }

    const atc = root.querySelector('[data-tqv-atc]');
    const atcText = root.querySelector('[data-tqv-atc-text]');
    if (atc) {
      atc.disabled = !variant.available;
      if (atcText) {
        atcText.textContent = variant.available
          ? atcText.dataset.addLabel || 'Add to cart'
          : atcText.dataset.soldOut || 'Sold out';
      }
    }
  };

  groups.forEach((group) => {
    group.addEventListener('click', (event) => {
      const btn = event.target.closest('.tqv-opt__btn');
      if (!btn) return;
      const index = Number(btn.dataset.optionIndex);
      selection[index] = btn.dataset.value;
      update();
    });
  });

  // Hover-preview the shade name on the colour option, like the PDP. Only on
  // hover-capable devices so a tap on touch just selects.
  if (colorGroup && window.matchMedia && window.matchMedia('(hover: hover)').matches) {
    const label = colorGroup.querySelector('[data-tqv-selected]');
    colorGroup.querySelectorAll('.tqv-opt__btn').forEach((btn) => {
      btn.addEventListener('mouseenter', () => {
        if (label && btn.dataset.value) label.textContent = btn.dataset.value;
      });
      btn.addEventListener('mouseleave', () => {
        if (label && colorIndex > -1 && selection[colorIndex] != null) label.textContent = selection[colorIndex];
      });
    });
  }

  // Quantity stepper.
  const qty = root.querySelector('[data-tqv-qty]');
  const readQty = () => Math.max(1, parseInt(qty && qty.value, 10) || 1);
  root.querySelector('[data-tqv-qty-dec]')?.addEventListener('click', () => {
    if (qty) qty.value = String(Math.max(1, readQty() - 1));
  });
  root.querySelector('[data-tqv-qty-inc]')?.addEventListener('click', () => {
    if (qty) qty.value = String(readQty() + 1);
  });
  qty?.addEventListener('change', () => {
    qty.value = String(readQty());
  });

  update();
}

document.addEventListener('click', (event) => {
  const trigger = event.target.closest('[data-quick-view-open]');
  if (!trigger) return;
  const url = trigger.dataset.productUrl;
  if (!url) return;
  event.preventDefault();
  open(url, trigger);
});
